import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, ArrowLeft, Check, Home, Sparkles, Building2, 
  KeyRound, Sofa, HelpCircle, Camera, Calendar, Clock, 
  User, Phone, Mail, MapPin, Upload, X, Loader2, 
  Calculator, TrendingDown, CheckCircle2, Star
} from 'lucide-react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteWizard = ({ language = 'en', onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [whatsappCountdown, setWhatsappCountdown] = useState(3);
  const fileInputRef = useRef(null);

  const WHATSAPP_NUMBER = '353899502499';

  useEffect(() => {
    if (currentStep !== 7 || !whatsappUrl) return;
    if (whatsappCountdown <= 0) {
      window.open(whatsappUrl, '_blank', 'noopener');
      return;
    }
    const t = setTimeout(() => setWhatsappCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [currentStep, whatsappUrl, whatsappCountdown]);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1 - Service Type
    service_type: '',
    // Step 2 - Property Details
    bedrooms: '',
    bathrooms: '',
    living_rooms: '',
    include_kitchen: null, // null = not answered, true = yes, false = no
    property_type: '',
    has_pets: null, // null = not answered, true = yes, false = no
    special_requests: '', // For cleaning inside/on top of cabinets, etc.
    extras: {}, // Changed to object: { extraId: quantity }
    // Step 3 - Photos
    photos: [],
    // Step 4 - Date & Time
    preferred_date: null,
    preferred_time: '',
    // Step 5 - Contact Info
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    eircode: '',
    address: '',
    message: ''
  });

  // Availability
  const [bookedSlots, setBookedSlots] = useState([]);
  const [pricing, setPricing] = useState([]);

  // Service types with descriptions
  const serviceTypes = [
    {
      id: 'regular',
      name: 'Regular House Cleaning',
      namePt: 'Limpeza Regular',
      description: 'Weekly or bi-weekly cleaning to keep your home fresh, organised and in order.',
      descriptionPt: 'Limpeza semanal ou quinzenal para manter sua casa fresca, organizada e em ordem.',
      icon: Home,
      popular: true
    },
    {
      id: 'deep',
      name: 'Deep Cleaning',
      namePt: 'Limpeza Profunda',
      description: 'A complete and detailed clean for homes that need extra attention in every room.',
      descriptionPt: 'Uma limpeza completa e detalhada para casas que precisam de atenção extra em todos os cômodos.',
      icon: Sparkles,
      popular: false
    },
    {
      id: 'office',
      name: 'Office Cleaning',
      namePt: 'Limpeza de Escritório',
      description: 'Professional cleaning for offices and commercial spaces.',
      descriptionPt: 'Limpeza profissional para escritórios e espaços comerciais.',
      icon: Building2,
      popular: false
    },
    {
      id: 'endoftenancy',
      name: 'End of Tenancy Cleaning',
      namePt: 'Limpeza Fim de Arrendamento',
      description: 'Ideal for the start or end of a rental contract, when the house needs to be spotless.',
      descriptionPt: 'Ideal para o início ou fim de um contrato de arrendamento, quando a casa precisa estar impecável.',
      icon: KeyRound,
      popular: false
    },
    {
      id: 'upholstery',
      name: 'Upholstery Cleaning',
      namePt: 'Limpeza de Estofados',
      description: 'Deep cleaning for sofas, carpets, mattresses and fabric furniture. Price upon consultation via photos.',
      descriptionPt: 'Limpeza profunda para sofás, carpetes, colchões e móveis de tecido. Preço sob consulta mediante fotos.',
      icon: Sofa,
      popular: false,
      consultationBased: true
    },
    {
      id: 'unsure',
      name: "I'm not sure / Help me choose",
      namePt: 'Não tenho certeza / Ajude-me a escolher',
      description: "Tell us about your home and we'll suggest the best option for you.",
      descriptionPt: 'Conte-nos sobre a sua casa e sugeriremos a melhor opção para você.',
      icon: HelpCircle,
      popular: false
    }
  ];

  // Property types
  const propertyTypes = [
    { id: 'apartment', name: 'Apartment', namePt: 'Apartamento' },
    { id: 'house', name: 'House', namePt: 'Casa' },
    { id: 'studio', name: 'Studio', namePt: 'Estúdio' },
    { id: 'office', name: 'Office', namePt: 'Escritório' }
  ];

  // Extra services with quantity support
  const extraServices = [
    { id: 'oven', name: 'Oven Cleaning', namePt: 'Limpeza de Forno', price: 30, unit: 'oven', unitPt: 'forno', maxQty: 3 },
    { id: 'fridge', name: 'Fridge Cleaning', namePt: 'Limpeza de Frigorífico', price: 20, unit: 'fridge', unitPt: 'frigorífico', maxQty: 3 },
    { id: 'windows', name: 'Window Cleaning (Interior)', namePt: 'Limpeza de Janelas (Interior)', price: 5, unit: 'window', unitPt: 'janela', maxQty: 20 }
  ];

  // Time slots
  const allTimeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', 
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', 
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', 
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'
  ];

  // Fetch pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing?region=ie`);
        setPricing(response.data);
      } catch (error) {
        
      }
    };
    fetchPricing();
  }, []);

  // Fetch booked slots when date changes
  // Helper to format date without timezone issues
  const formatLocalDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (formData.preferred_date) {
        try {
          const dateStr = formatLocalDate(formData.preferred_date);
          const response = await axios.get(`${API}/availability?date=${dateStr}&region=ie`);
          setBookedSlots(response.data.booked_slots || []);
        } catch (error) {
          
          setBookedSlots([]);
        }
      }
    };
    fetchBookedSlots();
  }, [formData.preferred_date]);

  // Check if time slot is available
  const isTimeSlotAvailable = (time, date) => {
    if (!date) return true;
    
    const dateStr = formatLocalDate(date);
    const isBooked = bookedSlots.some(slot => slot.date === dateStr && slot.time === time);
    if (isBooked) return false;
    
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (isWeekend) return true;
    
    const hour = parseInt(time.split(':')[0]);
    const minute = parseInt(time.split(':')[1]);
    const timeInMinutes = hour * 60 + minute;
    
    return timeInMinutes >= 14 * 60 + 30;
  };

  // Get available time slots for selected date
  const getAvailableTimeSlots = () => {
    if (!formData.preferred_date) return allTimeSlots;
    return allTimeSlots.filter(time => isTimeSlotAvailable(time, formData.preferred_date));
  };

  // Calculate estimated price based on time
  const calculateEstimate = () => {
    const bedrooms = parseInt(formData.bedrooms) || 0;
    const bathrooms = parseInt(formData.bathrooms) || 0;
    const livingRooms = parseInt(formData.living_rooms) || 0;
    const includeKitchen = formData.include_kitchen === true;

    // Hourly rates
    const hourlyRates = {
      regular: 23,
      deep: 31,
      office: 25,
      endoftenancy: 31,
      upholstery: 0, // Consultation based
      unsure: 23
    };

    // Time estimates in hours for REGULAR cleaning
    const regularTimeEstimates = {
      base: 1,        // Base time for halls, corridors, general areas
      bedroom: 0.5,   // 30 min per bedroom
      bathroom: 0.5,  // 30 min per bathroom
      livingRoom: 0.5, // 30 min per living room
      kitchen: 0.75   // 45 min for kitchen
    };

    // Time estimates in hours for DEEP cleaning (more intensive)
    const deepTimeEstimates = {
      base: 1.5,      // 1h 30min base
      bedroom: 1,     // 1h per bedroom
      bathroom: 1.5,  // 1h 30min per bathroom
      livingRoom: 1,  // 1h per living room
      kitchen: 1.5    // 1h 30min for kitchen
    };

    // Use appropriate time estimates based on service type
    const isDeepService = formData.service_type === 'deep' || formData.service_type === 'endoftenancy';
    const timeEstimates = isDeepService ? deepTimeEstimates : regularTimeEstimates;

    // Calculate total hours
    let totalHours = timeEstimates.base;
    totalHours += bedrooms * timeEstimates.bedroom;
    totalHours += bathrooms * timeEstimates.bathroom;
    totalHours += livingRooms * timeEstimates.livingRoom;
    
    if (includeKitchen) {
      totalHours += timeEstimates.kitchen;
    }

    // Get hourly rate
    const hourlyRate = hourlyRates[formData.service_type] || 23;

    // Calculate base price from hours
    let basePrice = totalHours * hourlyRate;

    // Add extras with quantities (fixed prices)
    const extrasTotal = Object.entries(formData.extras).reduce((sum, [extraId, quantity]) => {
      const extra = extraServices.find(e => e.id === extraId);
      return sum + ((extra?.price || 0) * quantity);
    }, 0);

    const ourPrice = basePrice + extrasTotal;
    const marketPrice = ourPrice * 1.25; // Competitors are ~25% higher
    const savings = marketPrice - ourPrice;

    return { ourPrice, marketPrice, savings, totalHours };
  };

  // Handle photo upload
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    if (formData.photos.length + files.length > 5) {
      alert(language === 'pt' ? 'Máximo de 5 fotos' : 'Maximum 5 photos');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return response.data.url;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedUrls]
      }));
    } catch (error) {
      
      alert(language === 'pt' ? 'Erro ao enviar foto' : 'Error uploading photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const selectedService = serviceTypes.find(s => s.id === formData.service_type);
      const estimate = calculateEstimate();
      
      // Format estimated time as "Xh Ym"
      const totalHours = estimate.totalHours || 0;
      const hours = Math.floor(totalHours);
      const minutes = Math.round((totalHours - hours) * 60);
      const estimatedTimeFormatted = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      
      // Format extras with quantities
      const extrasFormatted = Object.entries(formData.extras)
        .map(([id, qty]) => {
          const extra = extraServices.find(e => e.id === id);
          return `${qty}x ${extra?.name}`;
        })
        .join(', ') || 'None';

      const submitData = {
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        eircode: formData.eircode,
        service_type: language === 'pt' ? selectedService?.namePt : selectedService?.name,
        preferred_date: formData.preferred_date ? formatLocalDate(formData.preferred_date) : '',
        preferred_time: formData.preferred_time,
        message: `Property: ${formData.property_type}, Bedrooms: ${formData.bedrooms}, Bathrooms: ${formData.bathrooms}, Living Rooms: ${formData.living_rooms}, Kitchen: ${formData.include_kitchen === true ? 'Yes' : 'No'}. Pets: ${formData.has_pets === true ? 'Yes' : formData.has_pets === false ? 'No' : 'Not specified'}. Extras: ${extrasFormatted}. Special Requests: ${formData.special_requests || 'None'}. ${formData.message}`,
        photo_urls: formData.photos,
        region: 'ie',
        estimated_price: selectedService?.consultationBased ? null : estimate.ourPrice,
        // NEW: Add estimated time and property details
        estimated_time: selectedService?.consultationBased ? null : estimatedTimeFormatted,
        property_type: formData.property_type,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        living_rooms: parseInt(formData.living_rooms) || 0,
        has_kitchen: formData.include_kitchen === true,
        has_pets: formData.has_pets === true,
        special_requests: formData.special_requests || ''
      };

      await axios.post(`${API}/contact`, submitData);

      const dateStr = formData.preferred_date ? formatLocalDate(formData.preferred_date) : '';
      const serviceName = submitData.service_type || '';
      const values = {
        nome: submitData.name,
        servico: serviceName,
        data: dateStr,
        hora: formData.preferred_time || '',
        morada: `${formData.address || ''} ${formData.eircode || ''}`.trim(),
        estimativa: submitData.estimated_price ? `€${submitData.estimated_price}` : (language === 'pt' ? 'a confirmar' : 'to be confirmed'),
      };
      let template = language === 'pt'
        ? 'Olá Sancur! Acabei de pedir um orçamento no site.\n\nNome: {nome}\nServiço: {servico}\nData: {data} {hora}\nMorada: {morada}\nEstimativa: {estimativa}\n\nPodem confirmar a disponibilidade?'
        : "Hi Sancur! I've just requested a quote on your website.\n\nName: {nome}\nService: {servico}\nDate: {data} {hora}\nAddress: {morada}\nEstimate: {estimativa}\n\nCan you confirm availability?";
      try {
        const tpl = await axios.get(`${API}/whatsapp-templates`);
        template = tpl.data[language === 'pt' ? 'quote_pt' : 'quote_en'] || template;
      } catch (_) { /* use default */ }
      const waText = Object.entries(values).reduce((txt, [k, v]) => txt.split(`{${k}}`).join(v), template);
      setWhatsappUrl(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waText)}`);
      setWhatsappCountdown(3);
      
      setSubmitStatus({
        type: 'success',
        message: language === 'pt' 
          ? 'Orçamento enviado com sucesso! Entraremos em contacto em breve.'
          : 'Quote request sent successfully! We will contact you soon.'
      });

      // Move to success step
      setCurrentStep(7);

    } catch (error) {
      
      setSubmitStatus({
        type: 'error',
        message: language === 'pt' 
          ? 'Erro ao enviar. Tente novamente.'
          : 'Error submitting. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigation
  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.service_type !== '';
      case 2: return formData.bedrooms !== '' && formData.bathrooms !== '' && formData.living_rooms !== '' && formData.property_type !== '';
      case 3: return true; // Photos optional
      case 4: return formData.preferred_date && formData.preferred_time !== '';
      case 5: return formData.first_name !== '' && formData.phone !== '' && formData.email !== '' && formData.eircode !== '';
      case 6: return true; // Summary
      default: return true;
    }
  };

  const nextStep = () => {
    if (canProceed() && currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === 6) {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const getText = (en, pt) => language === 'pt' ? pt : en;

  // Step indicators
  const steps = [
    { num: 1, name: getText('Service', 'Serviço') },
    { num: 2, name: getText('Property', 'Imóvel') },
    { num: 3, name: getText('Photos', 'Fotos') },
    { num: 4, name: getText('Date', 'Data') },
    { num: 5, name: getText('Contact', 'Contacto') },
    { num: 6, name: getText('Summary', 'Resumo') }
  ];

  // Success screen
  if (currentStep === 7) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center text-center p-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {getText('Quote Request Sent!', 'Pedido de Orçamento Enviado!')}
        </h2>
        <p className="text-gray-600 mb-6 max-w-md">
          {getText(
            "Thank you! We'll review your request and contact you via WhatsApp or phone within 2 hours.",
            "Obrigado! Vamos analisar o seu pedido e entrar em contacto via WhatsApp ou telefone dentro de 2 horas."
          )}
        </p>
        <div className="w-full max-w-md mb-8 bg-[#25D366]/10 border border-[#25D366]/40 rounded-xl p-5" data-testid="whatsapp-redirect-box">
          <p className="text-sm text-gray-700 mb-3" data-testid="whatsapp-redirect-text">
            {whatsappCountdown > 0
              ? getText(`Opening WhatsApp in ${whatsappCountdown}s to continue with our team...`, `A abrir o WhatsApp em ${whatsappCountdown}s para continuar com a nossa equipa...`)
              : getText("Didn't open? Tap the button below to talk to us on WhatsApp.", 'Não abriu? Toque no botão abaixo para falar connosco no WhatsApp.')}
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-continue-button"
            className="inline-flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-3 px-6 rounded-full transition-colors"
          >
            <Phone className="w-5 h-5" />
            {getText('Continue on WhatsApp', 'Continuar no WhatsApp')}
          </a>
        </div>
        <div className="bg-gray-50 rounded-xl p-6 max-w-md">
          <p className="text-sm text-gray-500 mb-2">{getText('3 SIMPLE STEPS:', '3 PASSOS SIMPLES:')}</p>
          <div className="space-y-3 text-left">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#1a5c3a] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
              <p className="text-gray-700">{getText("We'll contact you to confirm details", "Entraremos em contacto para confirmar detalhes")}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#1a5c3a] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
              <p className="text-gray-700">{getText("We'll send you the final quote", "Enviaremos o orçamento final")}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#1a5c3a] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
              <p className="text-gray-700">{getText("Confirm and we'll be there!", "Confirme e estaremos lá!")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-2xl mx-auto">
      {/* Progress Header - Compact on mobile */}
      <div className="bg-gradient-to-r from-[#1a5c3a] to-[#2d7a50] p-3 sm:p-6 text-white">
        <h2 className="text-base sm:text-xl font-bold mb-3 sm:mb-4 text-center">
          {getText('Get Your Free Quote', 'Orçamento Grátis')}
        </h2>
        
        {/* Step indicators - Smaller on mobile */}
        <div className="flex justify-between items-center max-w-xs sm:max-w-md mx-auto">
          {steps.map((step, index) => (
            <div key={step.num} className="flex items-center">
              <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                currentStep > step.num 
                  ? 'bg-white text-[#1a5c3a]' 
                  : currentStep === step.num 
                    ? 'bg-white text-[#1a5c3a] ring-2 sm:ring-4 ring-white/30' 
                    : 'bg-white/20 text-white/70'
              }`}>
                {currentStep > step.num ? <Check size={14} className="sm:w-4 sm:h-4" /> : step.num}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-4 sm:w-10 h-0.5 mx-0.5 sm:mx-1 ${
                  currentStep > step.num ? 'bg-white' : 'bg-white/20'
                }`} />
              )}
            </div>
          ))}
        </div>
        <p className="text-center text-white/80 text-xs sm:text-sm mt-2 sm:mt-3">
          {steps[currentStep - 1]?.name}
        </p>
      </div>

      {/* Step Content */}
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Step 1: Service Type */}
        {currentStep === 1 && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              {getText('What type of cleaning?', 'Que tipo de limpeza?')}
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm">
              {getText(
                'Choose the option that best suits your needs.',
                'Escolha a opção que melhor se adequa.'
              )}
            </p>
            
            <div className="space-y-2 sm:space-y-3">
              {serviceTypes.map((service) => {
                const Icon = service.icon;
                const isSelected = formData.service_type === service.id;
                const isUnavailable = service.unavailable;
                
                return (
                  <button
                    key={service.id}
                    onClick={() => !isUnavailable && setFormData(prev => ({ ...prev, service_type: service.id }))}
                    disabled={isUnavailable}
                    className={`w-full p-3 sm:p-4 rounded-xl border-2 text-left transition-all relative ${
                      isUnavailable
                        ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                        : isSelected 
                          ? 'border-[#1a5c3a] bg-[#1a5c3a]/5' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`service-${service.id}`}
                  >
                    {service.popular && (
                      <span className="absolute -top-2 right-2 sm:right-4 px-2 py-0.5 bg-[#d4af37] text-white text-[10px] sm:text-xs font-bold rounded-full">
                        {getText('POPULAR', 'POPULAR')}
                      </span>
                    )}
                    {isUnavailable && (
                      <span className="absolute -top-2 right-2 sm:right-4 px-2 py-0.5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full">
                        {getText(service.unavailableMessage, service.unavailableMessagePt)}
                      </span>
                    )}
                    {service.consultationBased && !isUnavailable && (
                      <span className="absolute -top-2 right-2 sm:right-4 px-2 py-0.5 bg-blue-500 text-white text-[10px] sm:text-xs font-bold rounded-full">
                        {getText('Quote on request', 'Sob consulta')}
                      </span>
                    )}
                    <div className="flex items-center gap-2 sm:gap-4">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isUnavailable ? 'bg-gray-200 text-gray-400' :
                        isSelected ? 'bg-[#1a5c3a] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon size={16} className="sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm sm:text-base font-semibold ${
                          isUnavailable ? 'text-gray-400' :
                          isSelected ? 'text-[#1a5c3a]' : 'text-gray-900'
                        }`}>
                          {getText(service.name, service.namePt)}
                        </p>
                        <p className={`text-xs sm:text-sm line-clamp-1 sm:line-clamp-none ${
                          isUnavailable ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {getText(service.description, service.descriptionPt)}
                        </p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isUnavailable ? 'border-gray-300 bg-gray-200' :
                        isSelected ? 'border-[#1a5c3a] bg-[#1a5c3a]' : 'border-gray-300'
                      }`}>
                        {isSelected && !isUnavailable && <Check size={12} className="text-white" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Property Details */}
        {currentStep === 2 && (
          <div className="space-y-4 sm:space-y-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              {getText('About your property', 'Sobre o seu imóvel')}
            </h3>
            
            {/* Property Type */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Property Type', 'Tipo de Imóvel')} *
              </label>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {propertyTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setFormData(prev => ({ ...prev, property_type: type.id }))}
                    className={`p-2 sm:p-3 rounded-xl border-2 text-center text-sm sm:text-base transition-all ${
                      formData.property_type === type.id
                        ? 'border-[#1a5c3a] bg-[#1a5c3a]/5 text-[#1a5c3a] font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {getText(type.name, type.namePt)}
                  </button>
                ))}
              </div>
            </div>

            {/* Bedrooms */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Bedrooms', 'Quartos')} *
              </label>
              <div className="flex gap-1 sm:gap-2">
                {['0', '1', '2', '3', '4', '5+'].map((num) => (
                  <button
                    key={num}
                    onClick={() => setFormData(prev => ({ ...prev, bedrooms: num }))}
                    className={`flex-1 py-2 sm:py-3 text-sm sm:text-base rounded-xl border-2 transition-all ${
                      formData.bedrooms === num
                        ? 'border-[#1a5c3a] bg-[#1a5c3a] text-white font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Bathrooms */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Bathrooms', 'Casas de Banho')} *
              </label>
              <div className="flex gap-1 sm:gap-2">
                {['0', '1', '2', '3', '4+'].map((num) => (
                  <button
                    key={num}
                    onClick={() => setFormData(prev => ({ ...prev, bathrooms: num }))}
                    className={`flex-1 py-2 sm:py-3 text-sm sm:text-base rounded-xl border-2 transition-all ${
                      formData.bathrooms === num
                        ? 'border-[#1a5c3a] bg-[#1a5c3a] text-white font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Living Rooms */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Living Rooms', 'Salas de Estar')} *
              </label>
              <div className="flex gap-1 sm:gap-2">
                {['0', '1', '2', '3+'].map((num) => (
                  <button
                    key={num}
                    onClick={() => setFormData(prev => ({ ...prev, living_rooms: num }))}
                    className={`flex-1 py-2 sm:py-3 text-sm sm:text-base rounded-xl border-2 transition-all ${
                      formData.living_rooms === num
                        ? 'border-[#1a5c3a] bg-[#1a5c3a] text-white font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {getText('€15 per living room', '€15 por sala de estar')}
              </p>
            </div>

            {/* Kitchen */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Include Kitchen Cleaning?', 'Incluir Limpeza da Cozinha?')}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {getText(
                  'Full kitchen clean including counters, appliances exterior, and floor. €25 extra.',
                  'Limpeza completa da cozinha incluindo bancadas, exterior dos eletrodomésticos e chão. €25 extra.'
                )}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, include_kitchen: true }))}
                  className={`p-3 sm:p-4 rounded-xl border-2 text-center transition-all ${
                    formData.include_kitchen === true
                      ? 'border-[#1a5c3a] bg-[#1a5c3a]/5 text-[#1a5c3a] font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid="kitchen-yes"
                >
                  <span className="text-sm sm:text-base">{getText('Yes (+€25)', 'Sim (+€25)')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, include_kitchen: false }))}
                  className={`p-3 sm:p-4 rounded-xl border-2 text-center transition-all ${
                    formData.include_kitchen === false
                      ? 'border-[#1a5c3a] bg-[#1a5c3a]/5 text-[#1a5c3a] font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid="kitchen-no"
                >
                  <span className="text-sm sm:text-base">{getText('No', 'Não')}</span>
                </button>
              </div>
            </div>

            {/* Extras */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Extras (Optional)', 'Extras (Opcional)')}
              </label>
              <div className="space-y-2">
                {extraServices.map((extra) => {
                  const quantity = formData.extras[extra.id] || 0;
                  const isSelected = quantity > 0;
                  
                  return (
                    <div
                      key={extra.id}
                      className={`w-full p-2 sm:p-3 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-[#1a5c3a] bg-[#1a5c3a]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className={`text-xs sm:text-sm font-medium ${isSelected ? 'text-[#1a5c3a]' : 'text-gray-700'}`}>
                            {getText(extra.name, extra.namePt)}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            €{extra.price}/{getText(extra.unit, extra.unitPt)}
                          </span>
                        </div>
                        
                        {/* Quantity Controls - Compact */}
                        <div className="flex items-center gap-1 sm:gap-2">
                          {isSelected && (
                            <span className="text-xs sm:text-sm font-medium text-[#1a5c3a]">
                              €{extra.price * quantity}
                            </span>
                          )}
                          <div className="flex items-center bg-gray-100 rounded-lg">
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => {
                                  const newExtras = { ...prev.extras };
                                  if (quantity <= 1) {
                                    delete newExtras[extra.id];
                                  } else {
                                    newExtras[extra.id] = quantity - 1;
                                  }
                                  return { ...prev, extras: newExtras };
                                });
                              }}
                              className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-l-lg transition-colors text-sm ${
                                isSelected 
                                  ? 'hover:bg-gray-200 text-gray-700' 
                                  : 'text-gray-400 cursor-default'
                              }`}
                              disabled={!isSelected}
                            >
                              −
                            </button>
                            <span className={`w-6 sm:w-8 text-center text-sm font-medium ${isSelected ? 'text-[#1a5c3a]' : 'text-gray-400'}`}>
                              {quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => {
                                  const currentQty = prev.extras[extra.id] || 0;
                                  if (currentQty < extra.maxQty) {
                                    return {
                                      ...prev,
                                      extras: { ...prev.extras, [extra.id]: currentQty + 1 }
                                    };
                                  }
                                  return prev;
                                });
                              }}
                              className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-r-lg transition-colors text-sm ${
                                quantity < extra.maxQty 
                                  ? 'hover:bg-[#1a5c3a] hover:text-white text-gray-700' 
                                  : 'text-gray-400 cursor-default'
                              }`}
                              disabled={quantity >= extra.maxQty}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Has Pets Question */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Do you have pets at home?', 'Tem animais de estimação em casa?')}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {getText(
                  'We use pet-safe cleaning products and bring extra equipment for pet hair removal.',
                  'Utilizamos produtos de limpeza seguros para animais e trazemos equipamentos extra para remoção de pelos.'
                )}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, has_pets: true }))}
                  className={`p-3 sm:p-4 rounded-xl border-2 text-center transition-all flex items-center justify-center gap-2 ${
                    formData.has_pets === true
                      ? 'border-[#1a5c3a] bg-[#1a5c3a]/5 text-[#1a5c3a] font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid="has-pets-yes"
                >
                  <span className="text-lg">🐾</span>
                  <span className="text-sm sm:text-base">{getText('Yes', 'Sim')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, has_pets: false }))}
                  className={`p-3 sm:p-4 rounded-xl border-2 text-center transition-all ${
                    formData.has_pets === false
                      ? 'border-[#1a5c3a] bg-[#1a5c3a]/5 text-[#1a5c3a] font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid="has-pets-no"
                >
                  <span className="text-sm sm:text-base">{getText('No', 'Não')}</span>
                </button>
              </div>
            </div>

            {/* Special Requests / Observations */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Special Requests (Optional)', 'Pedidos Especiais (Opcional)')}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                {getText(
                  'Example: Clean inside cabinets, on top of cabinets, behind furniture, etc.',
                  'Exemplo: Limpar dentro dos armários, em cima dos armários, atrás dos móveis, etc.'
                )}
              </p>
              <textarea
                value={formData.special_requests}
                onChange={(e) => setFormData(prev => ({ ...prev, special_requests: e.target.value }))}
                placeholder={getText(
                  'Tell us any specific areas or items you would like us to focus on...',
                  'Diga-nos áreas ou itens específicos que gostaria que focássemos...'
                )}
                className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#1a5c3a] focus:ring-0 resize-none"
                rows={3}
                data-testid="special-requests"
              />
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {currentStep === 3 && (
          <div className="space-y-4 sm:space-y-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              {getText('Add photos (optional)', 'Adicionar fotos (opcional)')}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">
              {getText(
                'Photos help us give you a more accurate quote.',
                'Fotos ajudam a dar um orçamento mais preciso.'
              )}
            </p>

            {/* Upload Area */}
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#1a5c3a] hover:bg-[#1a5c3a]/5 transition-all"
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              {uploadingPhoto ? (
                <Loader2 className="w-12 h-12 mx-auto text-[#1a5c3a] animate-spin mb-4" />
              ) : (
                <Camera className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              )}
              <p className="font-medium text-gray-700">
                {getText('Click to upload photos', 'Clique para enviar fotos')}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {getText('Maximum 5 photos', 'Máximo 5 fotos')}
              </p>
            </div>

            {/* Uploaded Photos */}
            {formData.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {formData.photos.map((url, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={url} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Date & Time */}
        {currentStep === 4 && (
          <div className="space-y-4 sm:space-y-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              {getText('Choose date & time', 'Escolha data e hora')}
            </h3>
            
            {/* Info about availability */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs sm:text-sm text-blue-700">
              <p>
                {getText(
                  '📅 Weekdays: from 14:30. Weekends: All day.',
                  '📅 Dias úteis: após 14:30. Fim de semana: Todo o dia.'
                )}
              </p>
            </div>

            {/* Date Picker */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                {getText('Date', 'Data')} *
              </label>
              <DatePicker
                selected={formData.preferred_date}
                onChange={(date) => {
                  setFormData(prev => ({ ...prev, preferred_date: date, preferred_time: '' }));
                }}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                placeholderText={getText('Select a date', 'Selecione uma data')}
              />
            </div>

            {/* Time Slots */}
            {formData.preferred_date && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  {getText('Time', 'Hora')} *
                </label>
                <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                  {allTimeSlots.map((time) => {
                    const isAvailable = isTimeSlotAvailable(time, formData.preferred_date);
                    const isSelected = formData.preferred_time === time;
                    
                    return (
                      <button
                        key={time}
                        onClick={() => isAvailable && setFormData(prev => ({ ...prev, preferred_time: time }))}
                        disabled={!isAvailable}
                        className={`py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-[#1a5c3a] text-white border-[#1a5c3a]'
                            : isAvailable
                              ? 'border-gray-200 hover:border-[#1a5c3a] hover:bg-[#1a5c3a]/5'
                              : 'bg-gray-100 text-gray-400 border-gray-100 cursor-not-allowed line-through'
                        }`}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>
                {getAvailableTimeSlots().length === 0 && (
                  <p className="text-red-500 text-xs sm:text-sm mt-2">
                    {getText('No slots available. Try another date.', 'Sem horários. Tente outra data.')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 5: Contact Info */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {getText('Your contact details', 'Os seus dados de contacto')}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              {getText(
                "We'll use your details to send the quote and confirm the booking via WhatsApp or phone.",
                'Usaremos os seus dados para enviar o orçamento e confirmar a reserva via WhatsApp ou telefone.'
              )}
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {getText('First Name', 'Primeiro Nome')} *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {getText('Last Name', 'Último Nome')} *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                  placeholder="Smith"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getText('Mobile / WhatsApp', 'Telemóvel / WhatsApp')} *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                placeholder="+353 83 123 4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Eircode *
              </label>
              <input
                type="text"
                value={formData.eircode}
                onChange={(e) => setFormData(prev => ({ ...prev, eircode: e.target.value.toUpperCase() }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                placeholder="D02 X285"
              />
              <p className="text-xs text-gray-500 mt-1">
                {getText('Tip: Enter the Eircode for faster service', 'Dica: Insira o Eircode para um serviço mais rápido')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getText('Address (Optional)', 'Morada (Opcional)')}
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent"
                placeholder="123 Main Street, Dublin"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getText('Additional Notes (Optional)', 'Notas Adicionais (Opcional)')}
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent resize-none"
                placeholder={getText('Any special requests or notes...', 'Algum pedido especial ou notas...')}
              />
            </div>
          </div>
        )}

        {/* Step 6: Summary */}
        {currentStep === 6 && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {getText('Review your quote request', 'Revise o seu pedido de orçamento')}
            </h3>

            {/* Price Comparison */}
            {(() => {
              const estimate = calculateEstimate();
              const selectedService = serviceTypes.find(s => s.id === formData.service_type);
              const isConsultationBased = selectedService?.consultationBased;
              
              return (
                <div className="bg-gradient-to-r from-[#1a5c3a] to-[#2d7a50] rounded-xl p-6 text-white">
                  <div className="flex items-center gap-2 mb-4">
                    <Calculator size={20} />
                    <span className="font-semibold">{getText('Estimated Price', 'Preço Estimado')}</span>
                  </div>
                  
                  {isConsultationBased ? (
                    <div className="bg-white/20 rounded-lg p-4 text-center">
                      <p className="text-white/70 text-sm mb-2">
                        {getText('Price upon consultation', 'Preço sob consulta')}
                      </p>
                      <p className="text-2xl font-bold mb-2">
                        {getText('We will contact you', 'Entraremos em contacto')}
                      </p>
                      <p className="text-white/80 text-sm">
                        {getText(
                          'Send photos of your items for an accurate quote.',
                          'Envie fotos dos seus estofados para um orçamento preciso.'
                        )}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Time estimate */}
                      <div className="bg-white/10 rounded-lg p-3 mb-4 text-center">
                        <p className="text-white/70 text-xs">{getText('Estimated Duration', 'Duração Estimada')}</p>
                        <p className="text-lg font-bold">
                          ~{estimate.totalHours.toFixed(1)} {getText('hours', 'horas')}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-white/10 rounded-lg p-3">
                          <p className="text-white/70 text-xs">{getText('Dublin Average', 'Média Dublin')}</p>
                          <p className="text-xl font-bold line-through decoration-red-400">€{estimate.marketPrice.toFixed(0)}</p>
                        </div>
                        <div className="bg-white/20 rounded-lg p-3">
                          <p className="text-white/70 text-xs">{getText('Our Price', 'Nosso Preço')}</p>
                          <p className="text-2xl font-bold">€{estimate.ourPrice.toFixed(0)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 bg-[#d4af37] text-[#1a5c3a] rounded-lg px-4 py-2">
                        <TrendingDown size={18} />
                        <span className="font-bold">
                          {getText(`You save €${estimate.savings.toFixed(0)}!`, `Você poupa €${estimate.savings.toFixed(0)}!`)}
                        </span>
                      </div>
                      
                      {/* Estimate Disclaimer */}
                      <div className="mt-4 bg-white/10 rounded-lg p-3 text-center">
                        <p className="text-white/90 text-xs sm:text-sm">
                          {getText(
                            '⚠️ Time and price are estimates only. Final values may vary after on-site evaluation by our cleaning team based on the actual condition of the property.',
                            '⚠️ Tempo e preço são apenas estimativas. Os valores finais podem variar após avaliação presencial pela nossa equipa de limpeza, de acordo com o estado real do imóvel.'
                          )}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Summary Details */}
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{getText('Service', 'Serviço')}</span>
                <span className="font-medium">
                  {serviceTypes.find(s => s.id === formData.service_type)?.[language === 'pt' ? 'namePt' : 'name']}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{getText('Property', 'Imóvel')}</span>
                <span className="font-medium">
                  {propertyTypes.find(p => p.id === formData.property_type)?.[language === 'pt' ? 'namePt' : 'name']} - {formData.bedrooms} {getText('bed', 'quartos')}, {formData.bathrooms} {getText('bath', 'WC')}
                </span>
              </div>
              {Object.keys(formData.extras).length > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">{getText('Extras', 'Extras')}</span>
                  <span className="font-medium text-right">
                    {Object.entries(formData.extras).map(([id, qty]) => {
                      const extra = extraServices.find(e => e.id === id);
                      return `${qty}x ${extra?.[language === 'pt' ? 'namePt' : 'name']}`;
                    }).join(', ')}
                  </span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{getText('Date & Time', 'Data e Hora')}</span>
                <span className="font-medium">
                  {formData.preferred_date?.toLocaleDateString('en-IE')} - {formData.preferred_time}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{getText('Contact', 'Contacto')}</span>
                <span className="font-medium text-right">
                  {formData.first_name} {formData.last_name}<br />
                  <span className="text-sm text-gray-500">{formData.phone}</span>
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{getText('Location', 'Localização')}</span>
                <span className="font-medium">{formData.eircode}</span>
              </div>
              {formData.photos.length > 0 && (
                <div className="py-2 border-b">
                  <span className="text-gray-600">{getText('Photos', 'Fotos')}</span>
                  <div className="flex gap-2 mt-2">
                    {formData.photos.map((url, i) => (
                      <img key={i} src={url} alt="" className="w-12 h-12 rounded object-cover" />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {submitStatus.type === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                {submitStatus.message}
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-8">
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="flex-1 py-2.5 sm:py-3 px-3 sm:px-6 border-2 border-gray-300 rounded-xl text-sm sm:text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1 sm:gap-2"
            >
              <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
              {getText('Back', 'Voltar')}
            </button>
          )}
          
          <button
            onClick={nextStep}
            disabled={!canProceed() || isSubmitting}
            className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-6 rounded-xl text-sm sm:text-base font-medium transition-colors flex items-center justify-center gap-1 sm:gap-2 ${
              canProceed() && !isSubmitting
                ? 'bg-[#1a5c3a] text-white hover:bg-[#0f3d26]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="next-step-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={16} />
                {getText('Sending...', 'A enviar...')}
              </>
            ) : currentStep === 6 ? (
              <>
                {getText('Send', 'Enviar')}
                <Check size={16} />
              </>
            ) : (
              <>
                {getText('Next', 'Seguinte')}
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteWizard;
